'use strict'

const SockJS = require('sockjs-client')
const Stomp = require('webstomp-client')

/**
 * Constructor for StompIt library
 *
 * @param {String} endpoint
 * @param {Object=} options {debug: true, binary: false, heartbeat: {incoming: 0, outgoing: 0} }
 * @param {Object=} sockJsOptions
 */
module.exports = function (endpoint, options, sockJsOptions) {
  let socket, stomp
  let stompHeaders
  let eventHandlers = []
  let subscriptions = {}
  const defaultOptions = {
    reconnect: true,
    reconnectTimeout: 10000
  }

  const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

  options = Object.assign(defaultOptions, (options || {}))

  const isConnected = function () {
    return stomp && stomp.connected
  }

  const handleConnectError = function (err) {
    console.log('Stomp-it handleConnectError:', err)
  }

  return {
    // States: INIT -> CONNECTING -> CONNECTED <-> RECONNECTING -> DISCONNECTED
    state: 'INIT',
    reconnectTime: undefined,

    registerEventHandler (handler) {
      if (eventHandlers.indexOf(handler) === -1) {
        eventHandlers.push(handler)
      }
    },

    _dispatchEvent (event) {
      eventHandlers.forEach((handler) => {
        handler(event)
      })
    },

    /**
     * Create a connection to Stomp server
     *
     * @param {Object=} headers
     * @returns {Promise}
     */
    connect (headers) {
      stompHeaders = headers || {}

      if (this.state !== 'INIT' && this.state !== 'DISCONNECTED') {
        console.log('stomp-it connect called in state ' + this.state + '; ignoring.')
        return Promise.resolve(true)
      }

      this.state = 'CONNECTING'
      this._dispatchEvent({
        type: 'CONNECTING'
      })

      const handleSockEvent = (e) => {
        console.log('Stomp-it handleSockEvent:', e)
        if ((this.state === 'CONNECTED') && (e.type === 'close' || e.type === 'error')) {
          if (options.reconnect) {
            this.state = 'RECONNECTING'
            this.reconnectTime = Date.now()
            this._dispatchEvent({
              type: 'RECONNECTING'
            })
            doConnect()
              .then((payload) => {
                console.log('Stomp-it reconnected!:', payload)
                // resubscribe to channels
                Object.keys(subscriptions).forEach((key) => {
                  subscriptions[key].stompSubscription = stomp.subscribe(key, subscriptions[key].callback)
                })
                wait(1000).then(() => {
                  this._dispatchEvent({
                    type: 'RECONNECTED'
                  })
                })
              })
              .catch((err) => {
                console.log('Stomp-it reconnect failure (1)')
              })
          } else {
            this.state = 'DISCONNECTED'
            this._dispatchEvent({
              type: 'DISCONNECTED'
            })
          }
        } else if ((this.state === 'RECONNECTING') && (e.type === 'close' || e.type === 'error')) {
          if ((Date.now() - this.reconnectTime) < options.reconnectTimeout) {
            console.log('Stomp-it attempting another reconnect')
            wait(1000)
              .then(() => {
                let rv = doConnect()
                this._dispatchEvent({
                  type: 'RECONNECTING'
                })
                return rv
              })
              .then((payload) => {
                console.log('Stomp-it reconnected!:', payload)
                // resubscribe to channels
                Object.keys(subscriptions).forEach((key) => {
                  subscriptions[key].stompSubscription = stomp.subscribe(key, subscriptions[key].callback)
                })
                wait(1000).then(() => {
                  this._dispatchEvent({
                    type: 'RECONNECTED'
                  })  
                })
              })
              .catch((err) => {
                console.log('Stomp-it reconnect failure (2)')
              })
          } else {
            console.log('Stomp-it reconnect timeout')
            this.state = 'DISCONNECTED'
            this._dispatchEvent({
              type: 'DISCONNECTED'
            })
          }
        } else if (e.type === 'close' || e.type === 'error') {
          this.state = 'DISCONNECTED'
          this._dispatchEvent({
            type: 'DISCONNECTED'
          })
        }
      }

      const doConnect = () => {
        socket = new SockJS(endpoint, undefined, sockJsOptions)
        // socket.addEventListener('open', (e) => { handleSockEvent(e, this) })
        socket.addEventListener('close', (e) => { handleSockEvent(e, this) })
        socket.addEventListener('error', (e) => { handleSockEvent(e, this) })
        // socket.addEventListener('message', (e) => { handleSockEvent(e, this) })

        if (Stomp.default && Stomp.default.over) {
          stomp = Stomp.default.over(socket, options)
        } else {
          stomp = Stomp.over(socket, options)
        }

        return new Promise((resolve, reject) => {
          let resolveHandler = (payload) => {
            this.state = 'CONNECTED'
            this._dispatchEvent({
              type: 'CONNECTED'
            })
            resolve(payload)
          }
          let rejectHandler = (error) => {
            handleConnectError(error)
            reject(error)
          }
          stomp.connect(stompHeaders, resolveHandler, rejectHandler)
        })
      }

      return doConnect()
    },

    /**
     * Disconnect from Stomp server
     *
     * @returns {Promise}
     */
    disconnect () {
      if (!isConnected()) {
        return Promise.resolve(true)
      } else {
        this.state = 'DISCONNECTED' 
        return new Promise((resolve, reject) => {
          stomp.disconnect(resolve, reject)
        })
      }
    },

    /**
     * Subscribe to a channel and handle unique subscription to a channel
     * automatically.
     *
     * @param {String} channelPath
     * @param {Function} callback
     * @returns {Object} The subscription object
     */
     subscribe (channelPath, callback) {
      if (!isConnected()) {
        throw new Error('Not connected to server...')
      }
    
      this.unsubscribe(channelPath)
    
      subscriptions[channelPath] = {
        stompSubscription: stomp.subscribe(channelPath, callback),
        callback: callback
      }
      return subscriptions[channelPath].stompSubscription
    },
    
    /**
     * Unsubscribe from a channel.
     *
     * @param {String} channelPath
     * @returns {*|void}
     */
    unsubscribe (channelPath) {
      if (subscriptions[channelPath] !== undefined) {
        console.log('Unsubscribing from channel: ' + channelPath)
        subscriptions[channelPath].stompSubscription.unsubscribe()
        delete subscriptions[channelPath]
      }
    },

    /**
     *
     * @param {String} destination
     * @param {Object|String} payload
     * @param {Object=} headers
     * @returns {*|void}
     */
    send (destination, payload, headers) {
      if (typeof payload === 'object') {
        payload = JSON.stringify(payload)
      }
      return stomp.send(destination, payload, headers)
    }
  }
}